import React, { useMemo, useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, LinearProgress, Stack, TextField, Tooltip,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
    DataTable, INNER_RADIUS, MONO_FONT, MetricMatrix, PanelFrame, SectionFrame, StatusBadge, ToneBanner,
    accentColorFor, formatTimestamp, useDensity,
} from '../components/SharedFrames';
import useKuberaDesk from './useKuberaDesk';
import {
    actionLabel, bookMetrics, bookStatus, decisionText, eventRows, feedStatus, inr, positionLine, pts, runsByTime,
    serviceStatus, sparkPoints, stopGauge,
} from './kuberaView';

const timeOnly = (value) => {
    if (!value) return '—';
    const d = new Date(String(value).includes('T') || String(value).includes('Z') ? value : `${value}+05:30`);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        timeZone: 'Asia/Kolkata' });
};

const ConfirmDialog = ({ open, title, body, confirmWord, confirmLabel, tone = 'error', onCancel, onConfirm }) => {
    const [typed, setTyped] = useState('');
    const [note, setNote] = useState('');
    const ok = !confirmWord || typed.trim().toUpperCase() === confirmWord;
    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>{body}</Typography>
                {confirmWord ? (
                    <TextField fullWidth size="small" autoFocus label={`Type ${confirmWord} to confirm`} value={typed}
                        onChange={(e) => setTyped(e.target.value)} sx={{ mb: 1.5 }} />
                ) : null}
                <TextField fullWidth size="small" label="Note (optional, recorded in kubera_events)" value={note}
                    onChange={(e) => setNote(e.target.value)} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button color={tone} variant="contained" disabled={!ok}
                    onClick={() => { onConfirm(note); setTyped(''); setNote(''); }}>
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const StopGauge = ({ book }) => {
    const theme = useTheme();
    const g = stopGauge(book);
    if (!g) return null;
    return (
        <Box sx={{ my: 1 }}>
            <Box sx={{ position: 'relative' }}>
                <LinearProgress variant="determinate" value={g.frac * 100}
                    sx={{ height: 10, borderRadius: 5, backgroundColor: alpha(theme.palette.divider, 0.6),
                        '& .MuiLinearProgress-bar': { backgroundColor: accentColorFor(theme, g.tone) } }} />
                <Tooltip title="entry credit">
                    <Box sx={{ position: 'absolute', top: -3, bottom: -3, width: 2, left: `${g.creditFrac * 100}%`,
                        backgroundColor: theme.palette.text.primary }} />
                </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO_FONT }}>
                take-profit ← {g.text} → stop
            </Typography>
        </Box>
    );
};

const BookCard = ({ book, paused, onPause, onResume }) => {
    const { compact } = useDensity();
    const status = bookStatus(book);
    const isPaused = paused.includes(book.book);
    return (
        <Box sx={(theme) => ({ borderRadius: INNER_RADIUS, border: '1px solid', p: compact ? 1 : 1.4,
            borderColor: alpha(status.tone === 'error' ? theme.palette.error.main : theme.palette.divider, 0.82) })}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontWeight: 700, fontFamily: MONO_FONT }}>{book.book}</Typography>
                    <StatusBadge label={book.live ? 'LIVE' : 'PAPER'} tone={book.live ? 'error' : 'default'} />
                    <StatusBadge label={book.account || '—'} tone="primary" />
                    {book.executed && !book.live ? <StatusBadge label="REFERENCE" tone="default" /> : null}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                    <StatusBadge label={status.label} tone={status.tone} />
                    {isPaused ? (
                        <Button size="small" variant="outlined" onClick={() => onResume(book.book)}>Resume entries</Button>
                    ) : (
                        <Button size="small" variant="outlined" color="warning" onClick={() => onPause(book.book)}>Pause entries</Button>
                    )}
                </Stack>
            </Stack>
            <Typography variant="body2" sx={{ mt: 0.8, fontFamily: MONO_FONT }}>
                {positionLine(book.position) || `Flat${book.lastExitReason ? ` · last exit: ${book.lastExitReason} ${book.lastExitDate || ''}` : ''}${book.wait === 'Y' ? ' · waiting for a Supertrend flip' : ''}`}
            </Typography>
            <StopGauge book={book} />
            <Box sx={{ mt: 1 }}>
                <MetricMatrix metrics={bookMetrics(book)} columns={{ xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(6, minmax(0, 1fr))' }} />
            </Box>
            {book.halted ? (
                <Box sx={{ mt: 1 }}>
                    <ToneBanner tone="error" label="Halted" message={`${book.haltReason}${book.residual ? ` · still open at the broker: ${JSON.stringify(book.residual)}` : ''}. Resolve at the broker, then clear the halt with the service stopped (runbook).`} />
                </Box>
            ) : null}
            {(book.orphaned || []).length ? (
                <Box sx={{ mt: 1 }}>
                    <ToneBanner tone="error" label="Orphaned" message={`Live position(s) not managed by Kubera: ${book.orphaned.map((o) => `${o.short}/${o.long} x${o.qty}`).join(', ')}. Close them in Kite.`} />
                </Box>
            ) : null}
        </Box>
    );
};

const EquitySpark = ({ series }) => {
    const theme = useTheme();
    const sp = sparkPoints(series.points);
    if (!sp) return null;
    const up = sp.last >= sp.first;
    return (
        <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" sx={{ fontFamily: MONO_FONT, fontWeight: 700 }}>{series.book}</Typography>
                <Typography variant="caption" sx={{ fontFamily: MONO_FONT, color: up ? 'success.main' : 'error.main' }}>
                    {inr(sp.last - sp.first)}
                </Typography>
            </Stack>
            <svg viewBox="0 0 260 48" width="100%" height={48} role="img" aria-label={`${series.book} equity`}>
                <path d={sp.d} fill="none" strokeWidth="1.6"
                    stroke={up ? theme.palette.success.main : theme.palette.error.main} />
            </svg>
            <Typography variant="caption" color="text.secondary">
                {series.points[0].day} → {series.points[series.points.length - 1].day} · equity {inr(sp.last)}
            </Typography>
        </Box>
    );
};

const TRADE_COLUMNS = [
    { key: 'closedAt', label: 'Closed' },
    { key: 'book', label: 'Book', monospace: true },
    { key: 'side', label: 'Spread', render: (_v, r) => `${r.side === 'PE' ? 'Bull put' : 'Bear call'} ${r.sellK}/${r.buyK}` },
    { key: 'lots', label: 'Lots', align: 'right' },
    { key: 'credit', label: 'Credit', align: 'right', render: (v) => pts(v) },
    { key: 'buyClose', label: 'Closed at', align: 'right', render: (_v, r) => pts((r.buyClose ?? 0) - (r.sellClose ?? 0)) },
    { key: 'reasonLabel', label: 'Why it exited' },
    { key: 'netRs', label: 'Net', align: 'right', render: (v) => inr(v) },
    { key: 'walkSlippagePts', label: 'Depth slip (pts)', align: 'right', render: (v) => pts(v) },
];

const KuberaDesk = () => {
    const { data, equity, error, fetchedAt, control } = useKuberaDesk(true);
    const [dialog, setDialog] = useState(null);
    const [controlError, setControlError] = useState(null);
    const service = data?.service || {};
    const controls = data?.controls || {};
    const books = data?.books || [];
    const paused = controls.paused || [];
    const svc = serviceStatus(service);
    const feed = feedStatus(service.feed);
    const events = useMemo(() => eventRows(data?.events || []), [data]);
    const runs = useMemo(() => runsByTime(data?.decisions || []), [data]);
    const reference = (data?.latestDecisions || []).find((d) => !String(d.book).includes('@')) || (data?.latestDecisions || [])[0];
    const killFromFile = controls.killActive && !controls.kill;

    const run = async (action, book, note) => {
        setDialog(null);
        try {
            setControlError(null);
            await control(action, book, note);
        } catch (err) {
            setControlError(err.message);
        }
    };
    const mode = service.mode || service.nextStartMode;

    return (
        <SectionFrame
            accent="strategy"
            eyebrow="Strategy State"
            title="Kubera Strategy Desk"
            description="NIFTY hourly credit spreads (nifty1 config R v3). Paper books run on the prime account; in live mode every account in the KUBERA classification trades its own R@account book with its own fills, stops and sizing. Updates every 3 seconds."
            status={(
                <Stack direction="row" spacing={0.9} flexWrap="wrap" useFlexGap>
                    <StatusBadge label={mode ? String(mode).toUpperCase() : 'MODE ?'} tone={mode === 'live' ? 'error' : 'default'} />
                    <StatusBadge label={svc.label} tone={svc.tone} />
                    {service.running ? <StatusBadge label={feed.label} tone={feed.tone} /> : null}
                    {controls.killActive || controls.kill ? <StatusBadge label="KILL ACTIVE" tone="error" /> : null}
                    <StatusBadge label={error ? 'API offline' : fetchedAt ? 'API live' : 'connecting'}
                        tone={error ? 'error' : fetchedAt ? 'success' : 'default'} />
                </Stack>
            )}
        >
            <Stack spacing={1.2} sx={{ mb: 2 }}>
                {service.modeChangePending ? (
                    <ToneBanner tone="warning" label="Mode change pending"
                        message={`ca_strategy_configs kubera.live says ${String(service.nextStartMode).toUpperCase()}; the running service is ${String(service.mode).toUpperCase()}. It switches at the next start (09:00).`} />
                ) : null}
                {killFromFile ? (
                    <ToneBanner tone="error" label="Kill file"
                        message="The kill switch is active from the kill file on the strategy VM (/run/kubera/kill). Remove it there; Insights can only clear its own kill flag." />
                ) : null}
                {controlError ? <ToneBanner tone="error" label="Control failed" message={controlError} /> : null}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    {controls.kill ? (
                        <Button variant="contained" color="success" onClick={() => setDialog({ kind: 'unkill' })}>Clear kill switch</Button>
                    ) : (
                        <Button variant="contained" color="error" onClick={() => setDialog({ kind: 'kill' })}>Kill switch</Button>
                    )}
                    {paused.includes('all') ? (
                        <Button variant="outlined" onClick={() => run('resume', 'all', 'resume all from desk')}>Resume all entries</Button>
                    ) : (
                        <Button variant="outlined" color="warning" onClick={() => setDialog({ kind: 'pause', book: 'all' })}>Pause all entries</Button>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {`Prime ${service.prime || '—'} · config ${service.configHash || '—'} · build ${service.gitSha ? String(service.gitSha).slice(0, 8) : '—'} · started ${service.bootAt ? timeOnly(service.bootAt) : '—'} · last ${service.lastEvent || '—'}`}
                        {controls.cancelledAccounts?.length ? ` · CANCEL in ca_job_control: ${controls.cancelledAccounts.join(', ')}` : ''}
                    </Typography>
                </Stack>
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(12, minmax(0, 1fr))' }, gap: 2 }}>
                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 12' } }}>
                    <PanelFrame accent="strategy" eyebrow="Books" title="Positions per book"
                        description="Each book's own spread against its own stop (1.5x its credit) and take-profit, MTM and next entry size. Live books first.">
                        {books.length ? (
                            <Stack spacing={1.2}>
                                {books.map((b) => (
                                    <BookCard key={b.book} book={b} paused={paused}
                                        onPause={(name) => setDialog({ kind: 'pause', book: name })}
                                        onResume={(name) => run('resume', name, 'resume from desk')} />
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary">No Kubera state yet: the service writes it at its first start.</Typography>
                        )}
                    </PanelFrame>
                </Box>
                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 6' } }}>
                    <PanelFrame accent="strategy" eyebrow="Signal" title={reference ? `Latest run ${reference.time}` : 'Latest run'}
                        description="Hourly NIFTY indicators the decision used (computed once, shared by every book with the same parameters).">
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{decisionText(reference)}</Typography>
                        <Stack spacing={0.6} sx={{ mt: 1.2 }}>
                            {(data?.latestDecisions || []).map((d) => {
                                const a = actionLabel(d);
                                return (
                                    <Stack key={d.book} direction="row" spacing={1} alignItems="center">
                                        <Typography variant="caption" sx={{ fontFamily: MONO_FONT, minWidth: 150 }}>{d.book}</Typography>
                                        <StatusBadge label={a.label} tone={a.tone} />
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </PanelFrame>
                </Box>
                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 6' } }}>
                    <PanelFrame accent="strategy" eyebrow="Today" title="Decision runs"
                        description="10:15 … 15:15 and 15:29: what each book did and why.">
                        {runs.length ? (
                            <Stack divider={<Divider flexItem />} spacing={1}>
                                {runs.map((g) => (
                                    <Stack key={g.time} direction="row" spacing={1.2} alignItems="flex-start">
                                        <Typography variant="caption" sx={{ fontFamily: MONO_FONT, minWidth: 44 }}>{g.time}</Typography>
                                        <Stack spacing={0.4}>
                                            {g.rows.map((d) => {
                                                const a = actionLabel(d);
                                                return (
                                                    <Typography key={d.book} variant="caption" sx={{ fontFamily: MONO_FONT }}>
                                                        {`${d.book}: ${a.label}${d.why && !a.label.includes(d.why) ? ` (${d.why})` : ''}`}
                                                    </Typography>
                                                );
                                            })}
                                        </Stack>
                                    </Stack>
                                ))}
                            </Stack>
                        ) : <Typography variant="body2" color="text.secondary">No runs yet today.</Typography>}
                    </PanelFrame>
                </Box>
                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 12' } }}>
                    <DataTable title="Recent trades" accent="strategy"
                        description="Closed trades, newest first: fills, why each exited, net after charges, and depth-walk slippage at real size (paper)."
                        columns={TRADE_COLUMNS} rows={data?.trades || []} emptyMessage="No closed trades yet." />
                </Box>
                {(equity?.series || []).length ? (
                    <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 12' } }}>
                        <PanelFrame accent="strategy" eyebrow="Equity" title="Equity per book (120 days)"
                            description="End-of-day corpus + mark-to-market per book.">
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                                {equity.series.map((s) => <EquitySpark key={s.book} series={s} />)}
                            </Box>
                        </PanelFrame>
                    </Box>
                ) : null}
                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 12' } }}>
                    <Tooltip title={fetchedAt ? `Last refreshed ${formatTimestamp(fetchedAt)}` : ''}>
                        <Box>
                            <PanelFrame accent="strategy" eyebrow="Operations" title="Today's events"
                                description="Pre-flight, halts, feed, controls, reconciliation and alerts, newest first.">
                                {events.length ? (
                                    <Stack divider={<Divider flexItem />} spacing={0.8} sx={{ maxHeight: 360, overflowY: 'auto' }}>
                                        {events.map((e, i) => (
                                            <Stack key={`${e.at}-${i}`} direction="row" spacing={1.2} alignItems="flex-start">
                                                <Typography variant="caption" sx={{ fontFamily: MONO_FONT, minWidth: 64 }}>{timeOnly(e.at)}</Typography>
                                                <Typography variant="body2" sx={(theme) => ({ lineHeight: 1.5,
                                                    color: e.tone === 'default' ? undefined : accentColorFor(theme, e.tone) })}>
                                                    {e.text}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                ) : <Typography variant="body2" color="text.secondary">No events today.</Typography>}
                            </PanelFrame>
                        </Box>
                    </Tooltip>
                </Box>
            </Box>

            <ConfirmDialog
                open={dialog?.kind === 'kill'}
                title="Activate the Kubera kill switch?"
                body={mode === 'live'
                    ? 'Every LIVE book closes its spread at market at the next minute and all entries stop on every account. The paper books keep running.'
                    : 'The paper reference book R closes at the next minute and all entries stop. Nothing reaches Zerodha in paper mode.'}
                confirmWord="KILL" confirmLabel="Kill" tone="error"
                onCancel={() => setDialog(null)} onConfirm={(note) => run('kill', null, note)}
            />
            <ConfirmDialog
                open={dialog?.kind === 'unkill'}
                title="Clear the kill switch?"
                body="Entries resume at the next decision run on every book that is not otherwise paused. Positions closed by the kill are not reopened."
                confirmLabel="Clear kill" tone="success"
                onCancel={() => setDialog(null)} onConfirm={(note) => run('unkill', null, note)}
            />
            <ConfirmDialog
                open={dialog?.kind === 'pause'}
                title={dialog?.book === 'all' ? 'Pause entries on every book?' : `Pause entries on ${dialog?.book}?`}
                body="No new spreads are opened. Open positions keep their stops, take-profit and every other exit."
                confirmLabel="Pause entries" tone="warning"
                onCancel={() => setDialog(null)} onConfirm={(note) => run('pause', dialog?.book, note)}
            />
        </SectionFrame>
    );
};

export default KuberaDesk;
